import React from 'react'
import { useParams, Link } from 'react-router-dom'
import useSWR from 'swr'
import { api } from '../services/api'
import { Badge, Button, Panel } from '../components'
import pageStyles from '../styles/page.module.scss'
import formStyles from '../styles/forms.module.scss'

interface TeamPlayer {
  tc_competitor_id: string | null
  tc_name: string | null
  ext_name: string | null
  tc_short_name: string | null
  ext_short_name: string | null
}

interface Team {
  tc_team_id: string
  ext_competitor_id: string | null
  tc_name: string | null
  ext_name: string | null
  tc_abbreviation: string | null
  ext_abbreviation: string | null
  player1: TeamPlayer
  player2: TeamPlayer
  sport_name: string | null
  catalog_source_name: string | null
  effective_virtual: boolean
  created_at: string | null
  tc_updated_at: string | null
  ext_updated_at: string | null
}

interface TeamDetailResponse {
  team: Team
  timestamp: string
}

const TeamDetail: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()

  const { data, isLoading, error, mutate } = useSWR(
    teamId ? ['team-detail', teamId] : null,
    () => api.get(`/teams/${teamId}`),
    { revalidateOnFocus: false }
  )

  const response: TeamDetailResponse | null = data?.body ? JSON.parse(data.body) : data
  const team = response?.team

  const getDisplayName = (tcValue: string | null, extValue: string | null): string => {
    return tcValue || extValue || '—'
  }

  const getTeamName = (): string => {
    if (!team) return 'Unknown Team'
    return team.tc_name || team.ext_name || 'Unknown Team'
  }

  const getPlayerDisplayName = (player: TeamPlayer | null): string => {
    if (!player) return '—'
    return player.tc_name || player.ext_name || '—'
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading team</h2>
          <p>Failed to fetch team details: {(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  if (isLoading || !team) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading team details...</h3>
          <p>Please wait while we fetch the team data.</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className={pageStyles.pageHeader}>
        <h1>{getTeamName()}</h1>
        <p>
          {team.ext_competitor_id && (
            <code className="id-code">{team.ext_competitor_id}</code>
          )}
          {' '}
          {team.sport_name && <Badge variant="primary">{team.sport_name}</Badge>}
          {' '}
          {team.catalog_source_name && <Badge variant="secondary">{team.catalog_source_name}</Badge>}
          {' '}
          {team.effective_virtual && <Badge variant="warning">Placeholder</Badge>}
        </p>
      </div>

      <Panel>
        <Panel.Header>
          <h2>Team Information</h2>
        </Panel.Header>
        <div className={formStyles.fieldGroup}>
          <label>Team Name</label>
          <input type="text" value={getDisplayName(team.tc_name, team.ext_name)} disabled />
        </div>
        <div className={formStyles.fieldGroup}>
          <label>Abbreviation</label>
          <input type="text" value={getDisplayName(team.tc_abbreviation, team.ext_abbreviation)} disabled />
        </div>
        <div className={formStyles.fieldGroup}>
          <label>Player 1</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="text" value={getPlayerDisplayName(team.player1)} disabled style={{ flex: 1 }} />
            {team.player1?.tc_competitor_id && (
              <Link to={`/players/${team.player1.tc_competitor_id}`}>
                <Button size="small">View</Button>
              </Link>
            )}
          </div>
        </div>
        <div className={formStyles.fieldGroup}>
          <label>Player 2</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="text" value={getPlayerDisplayName(team.player2)} disabled style={{ flex: 1 }} />
            {team.player2?.tc_competitor_id && (
              <Link to={`/players/${team.player2.tc_competitor_id}`}>
                <Button size="small">View</Button>
              </Link>
            )}
          </div>
        </div>
        <div className={formStyles.fieldGroup}>
          <label>Sport</label>
          <input type="text" value={team.sport_name || '—'} disabled />
        </div>
        <div className={formStyles.fieldGroup}>
          <label>Data Source</label>
          <input type="text" value={team.catalog_source_name || '—'} disabled />
        </div>
      </Panel>
    </div>
  )
}

export default TeamDetail
